import React from 'react';
import { observer } from 'mobx-react';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { store } from 'stores/Store';

interface InterruptControllerProps {
  setIsTyping: (isTyping: boolean) => void;
  setIsLoading: (isLoading: boolean) => void;
  setIsVoicePlaying: (isVoicePlaying: boolean) => void;
  controllerRef: React.MutableRefObject<AbortController | null>;
}

export interface InterruptControllerRef {
  isForceStopped: () => boolean;
  handleInterrupt: () => void;
}

const InterruptController = React.forwardRef<InterruptControllerRef, InterruptControllerProps>(
  ({ setIsTyping, setIsLoading, setIsVoicePlaying, controllerRef }, ref) => {
    const forceStopRef = React.useRef<boolean>(false);

    const handleInterrupt = React.useCallback(() => {
      // Set force stop flag
      forceStopRef.current = true;

      // Abort any ongoing requests
      if (controllerRef.current) {
        controllerRef.current.abort();
        controllerRef.current = new AbortController();
      }

      // Reset all states
      setIsTyping(false);
      setIsLoading(false);
      setIsVoicePlaying(false);

      // Reset force stop after a short delay
      setTimeout(() => {
        forceStopRef.current = false;
      }, 1000);
    }, [controllerRef, setIsTyping, setIsLoading, setIsVoicePlaying]);

    // Listen for keyboard interrupt (Escape key)
    React.useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          handleInterrupt();
        }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleInterrupt]);

    // Expose the force stop flag and interrupt handler
    React.useImperativeHandle(ref, () => ({
      isForceStopped: () => forceStopRef.current,
      handleInterrupt
    }));

    return null; // This is a controller component, no UI
  }
);

export default observer(InterruptController);